import { Action, Location } from 'history';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

interface PendingNavigation {
    action: Action;
    location: Location;
}

export interface UnsavedChangesNavigationGuard {
    isNavigationBlocked: boolean;
    discardChangesAndContinue: () => void;
    stayOnPage: () => void;
}

export const useUnsavedChangesNavigationGuard = (shouldBlock: boolean): UnsavedChangesNavigationGuard => {
    const history = useHistory();
    const unblockRef = React.useRef<(() => void) | undefined>();
    const [pendingNavigation, setPendingNavigation] = React.useState<PendingNavigation>();

    React.useEffect(() => {
        if (!shouldBlock) {
            setPendingNavigation(undefined);
            return undefined;
        }

        const unblock = history.block((location, action) => {
            setPendingNavigation({ action, location });
            return false;
        });
        unblockRef.current = unblock;

        return () => {
            unblock();
            if (unblockRef.current === unblock) {
                unblockRef.current = undefined;
            }
        };
    }, [history, shouldBlock]);

    const stayOnPage = React.useCallback(() => {
        setPendingNavigation(undefined);
    }, []);

    const discardChangesAndContinue = React.useCallback(() => {
        if (!pendingNavigation) {
            return;
        }

        unblockRef.current?.();
        unblockRef.current = undefined;

        switch (pendingNavigation.action) {
            case 'POP':
                history.goBack();
                break;
            case 'REPLACE':
                history.replace(pendingNavigation.location);
                break;
            case 'PUSH':
                history.push(pendingNavigation.location);
                break;
        }
    }, [history, pendingNavigation]);

    return {
        isNavigationBlocked: pendingNavigation !== undefined,
        discardChangesAndContinue,
        stayOnPage,
    };
};
